import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';
import { useToastStore } from '../../../stores/toast-store';

/**
 * useConfirmCustomerIdentity — Item 391-B (cadastro progressivo, promoção por
 * operador). O operador confirma que o contato É o titular: o backend promove o
 * vínculo de contrato a `operator_confirmed` + marca a identidade do canal
 * verificada-via-operador (TTL 90d) — a confiança PERSISTE (não se re-pergunta).
 *
 * On success invalida o detalhe da conversa (o selo de identidade reflete
 * "verificada"). Espelho de `useClaimConversation`. Gate backend:
 * `customer-registry-confirm`. 409 quando não há vínculo de contrato a confirmar.
 */
export function useConfirmCustomerIdentity(conversationId: string | undefined): {
  confirm: () => Promise<void>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () => {
      if (conversationId === undefined) {
        throw new Error('conversationId obrigatório para confirmar titular');
      }
      return api.confirmCustomerIdentity(conversationId);
    },
    onSuccess: () => {
      if (conversationId === undefined) return;
      void queryClient.invalidateQueries({ queryKey: ['conversation', conversationId] });
      void queryClient.invalidateQueries({ queryKey: ['conversations'] });
      useToastStore.getState().push({
        variant: 'success',
        title: 'Titular confirmado',
        description:
          'Identidade marcada como verificada por você (válida pela política de validade do tenant).',
        durationMs: 3500,
      });
    },
    onError: (err) => {
      useToastStore.getState().push({
        variant: 'error',
        title: 'Não foi possível confirmar o titular',
        description: err instanceof Error ? err.message : 'Erro desconhecido',
        durationMs: 6000,
      });
    },
  });

  return {
    confirm: async () => {
      await mutation.mutateAsync();
    },
    isPending: mutation.isPending,
  };
}
